/**
 * Streaks, and the promise that they are never a debt
 * ([Gamification](../../docs/GAMIFICATION.md)).
 *
 * A streak counts consecutive days with at least one completed lesson, in the
 * **child's own timezone** — so a day here is a calendar date, `YYYY-MM-DD`,
 * decided by the client and passed in. Doing the arithmetic in UTC instants
 * would give a child in Copenhagen a day that ends at 2am
 * ([D-021](../../docs/DECISIONS.md#d-021)).
 *
 * Two rules make this a reason to come back rather than something to be afraid
 * of losing:
 *
 * - **Vacation is free and explicit.** A child who says in advance that they
 *   will be away keeps their streak ([D-022](../../docs/DECISIONS.md#d-022)).
 *   It never costs acorns, because making a child pay to not be punished is
 *   precisely the pattern this app refuses.
 * - **Breaking it costs nothing but the number.** The longest is kept, the
 *   first day back pays a bonus, and nothing here reports a loss for the app to
 *   announce.
 */
/** 3, 7, 14 … the days worth a celebration and a chest. */
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100, 200, 365];
/** Acorns for reaching each one. Rising, so the later ones stay worth it. */
export const MILESTONE_ACORNS = {
    3: 50,
    7: 75,
    14: 120,
    30: 200,
    60: 275,
    100: 350,
    200: 425,
    365: 500,
};
export const NO_STREAK = { current: 0, longest: 0, lastDay: null };
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;
/**
 * A calendar date as a count of days.
 *
 * Parsed as UTC midnight on purpose: these are *dates*, not instants, and the
 * only thing done with them is subtraction. Parsing them in the machine's local
 * zone would make the answer depend on which server ran it.
 */
function dayNumber(day) {
    if (!DAY.test(day))
        return null;
    const at = Date.parse(`${day}T00:00:00.000Z`);
    if (Number.isNaN(at))
        return null;
    // Round-trip check: `2026-02-31` parses to March 3rd, and a date that is not
    // the date it says it is has no business deciding whether a streak survived.
    const back = new Date(at).toISOString().slice(0, 10);
    return back === day ? at / MS_PER_DAY : null;
}
/** Whole days from `from` to `to`, or null if either is not a real date. */
export function daysBetween(from, to) {
    const a = dayNumber(from);
    const b = dayNumber(to);
    return a === null || b === null ? null : b - a;
}
/** The day after this one. */
export function nextDay(day) {
    const at = dayNumber(day);
    if (at === null)
        return day;
    return new Date((at + 1) * MS_PER_DAY).toISOString().slice(0, 10);
}
/** This day moved by `n` days, either way. A day that is not a real date is returned as it came. */
export function addDays(day, n) {
    const at = dayNumber(day);
    if (at === null)
        return day;
    return new Date((at + n) * MS_PER_DAY).toISOString().slice(0, 10);
}
/** Every day from `from` to `to`, exclusive at both ends. */
export function daysBetweenExclusive(from, to) {
    const gap = daysBetween(from, to);
    if (gap === null || gap <= 1)
        return [];
    const out = [];
    let day = nextDay(from);
    for (let i = 1; i < gap; i += 1) {
        out.push(day);
        day = nextDay(day);
    }
    return out;
}
/**
 * Record that a lesson was finished on a day.
 *
 * `onVacation` answers "was this child excused from that day". Every day in the
 * gap must be excused for the streak to survive it: one unexcused day in the
 * middle of a fortnight away is still a break, which is why this asks per day
 * rather than taking a range.
 *
 * Playing *during* a vacation is allowed and encouraged — the day counts as an
 * ordinary day and the vacation is not consumed, which falls out of treating
 * today as played rather than as excused.
 */
export function playedOn(streak, day, onVacation = () => false) {
    if (dayNumber(day) === null) {
        return { streak, milestone: null, comeback: false, counted: false };
    }
    const previous = streak.lastDay;
    // Already counted today. Finishing a second lesson is not a second day, and
    // it must not re-pay the milestone either.
    if (previous !== null && daysBetween(previous, day) === 0) {
        return { streak, milestone: null, comeback: false, counted: true };
    }
    // A day *before* the last one counted. Backdated, or a device with a clock a
    // week out. Nothing moves — a streak must never be shortened by an old day.
    const gap = previous === null ? null : daysBetween(previous, day);
    if (gap !== null && gap < 0) {
        return { streak, milestone: null, comeback: false, counted: false };
    }
    const continues = previous !== null &&
        (gap === 1 || daysBetweenExclusive(previous, day).every((d) => onVacation(d)));
    const current = continues ? streak.current + 1 : 1;
    const next = {
        current,
        longest: Math.max(streak.longest, current),
        lastDay: day,
    };
    return {
        streak: next,
        milestone: STREAK_MILESTONES.includes(current)
            ? current
            : null,
        // A first-ever day is not a comeback: there is nothing to have come back
        // from, and greeting a new child with "you're back!" is nonsense.
        comeback: previous !== null && !continues,
        counted: true,
    };
}
/** XP for today, given the streak it is part of. */
export const streakXp = (current, perDay, cap) => perDay * Math.min(current, cap);
//# sourceMappingURL=streak.js.map